// KHLIFI HOUCEM / FORMATEUR CLOUD && DEVSECOPS
// BackupAdminTab - Backup & Restore Administration Panel

import { useState, useEffect, useCallback } from "react";
import { backupAPI, workspaceAPI } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import WorkspaceExportTab from "@/components/admin/WorkspaceExportTab";
import RestoreHistoryTab from "@/components/admin/RestoreHistoryTab";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  HardDrive,
  Download,
  Trash2,
  Play,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  Shield,
  Database,
  FolderArchive,
  Settings,
  AlertTriangle,
  RotateCcw,
  Save,
  Layers,
} from "lucide-react";
import { toast } from "sonner";
import { formatRelativeTime } from "@/lib/utils";

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

export default function BackupAdminTab() {
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [restoreJobs, setRestoreJobs] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [policy, setPolicy] = useState({
    retention_days: 30,
    auto_backup_enabled: false,
    auto_backup_interval_hours: 24,
    backup_types: ["database", "uploads"],
    max_backups: 10,
  });
  const [savingPolicy, setSavingPolicy] = useState(false);

  // Backup launch state
  const [backupDialogOpen, setBackupDialogOpen] = useState(false);
  const [backupType, setBackupType] = useState("full");
  const [backupDescription, setBackupDescription] = useState("");
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState("");
  const [launching, setLaunching] = useState(false);

  // Restore state
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [restorePassword, setRestorePassword] = useState("");
  const [restoreConfirm, setRestoreConfirm] = useState(false);
  const [restoring, setRestoring] = useState(false);

  // Delete state
  const [deleteJobId, setDeleteJobId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [jobsRes, policyRes, restoreRes, wsRes] = await Promise.all([
        backupAPI.listJobs({ limit: 50 }).catch(() => ({ data: [] })),
        backupAPI.getPolicy().catch(() => ({ data: policy })),
        backupAPI.getRestoreStatus(5).catch(() => ({ data: [] })),
        workspaceAPI.list().catch(() => ({ data: [] })),
      ]);
      setJobs(jobsRes.data || []);
      if (policyRes.data) setPolicy(prev => ({ ...prev, ...policyRes.data }));
      setRestoreJobs(restoreRes.data || []);
      setWorkspaces(wsRes.data || []);
    } catch (error) {
      console.error("Failed to load backup data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 15000);
    return () => clearInterval(interval);
  }, [loadData]);

  // Compute stats from jobs list directly (avoids stats API issues)
  const completedJobs = jobs.filter(j => j.status === "completed");
  const stats = {
    total_backups: completedJobs.length,
    total_size_bytes: completedJobs.reduce((sum, j) => sum + (j.size_bytes || 0), 0),
    failed_count: jobs.filter(j => j.status === "failed").length,
    running_count: jobs.filter(j => j.status === "running").length,
    last_backup: completedJobs.length > 0 ? completedJobs[0] : null,
  };

  const handleSavePolicy = async () => {
    setSavingPolicy(true);
    try {
      await backupAPI.updatePolicy(policy);
      toast.success("Politique de backup mise à jour");
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSavingPolicy(false);
    }
  };

  const handleLaunchBackup = async () => {
    if (backupType === "workspace" && !selectedWorkspaceId) {
      toast.error("Veuillez sélectionner un workspace");
      return;
    }
    setLaunching(true);
    try {
      if (backupType === "workspace") {
        await backupAPI.workspaceBackup(selectedWorkspaceId);
      } else {
        await backupAPI.runBackup({
          backup_type: backupType,
          description: backupDescription || undefined,
        });
      }
      toast.success("Backup lancé avec succès !");
      setBackupDialogOpen(false);
      setBackupDescription("");
      setSelectedWorkspaceId("");
      setBackupType("full");
      setTimeout(loadData, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors du lancement");
    } finally {
      setLaunching(false);
    }
  };

  const handleRestore = async () => {
    if (!selectedBackup || !restorePassword) return;
    setRestoring(true);
    try {
      if (selectedBackup.backup_type === "workspace" && selectedBackup.workspace_id) {
        await backupAPI.workspaceRestore(selectedBackup.workspace_id, {
          backup_id: selectedBackup.id,
          restore_type: "workspace",
          workspace_id: selectedBackup.workspace_id,
          confirm_password: restorePassword,
        });
      } else {
        await backupAPI.restore({
          backup_id: selectedBackup.id,
          restore_type: selectedBackup.backup_type === "workspace" ? "workspace" : "full",
          workspace_id: selectedBackup.workspace_id || undefined,
          confirm_password: restorePassword,
        });
      }
      toast.success("Restauration lancée !");
      setRestoreDialogOpen(false);
      setRestorePassword("");
      setRestoreConfirm(false);
      setSelectedBackup(null);
      setTimeout(loadData, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la restauration");
    } finally {
      setRestoring(false);
    }
  };

  const handleDeleteBackup = async () => {
    if (!deleteJobId) return;
    setDeleting(true);
    try {
      await backupAPI.deleteJob(deleteJobId);
      toast.success("Backup supprimé");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleting(false);
      setDeleteJobId(null);
    }
  };

  const openRestoreDialog = (job) => {
    setSelectedBackup(job);
    setRestorePassword("");
    setRestoreConfirm(false);
    setRestoreDialogOpen(true);
  };

  const getWorkspaceName = (wsId) => {
    const ws = workspaces.find(w => w.id === wsId);
    return ws ? ws.name : wsId?.slice(0, 8);
  };

  const getStatusBadge = (status) => {
    const map = {
      completed: { label: "Terminé", variant: "default", icon: CheckCircle, color: "text-green-600" },
      running: { label: "En cours", variant: "secondary", icon: Loader2, color: "text-blue-600" },
      failed: { label: "Échoué", variant: "destructive", icon: XCircle, color: "text-red-600" },
    };
    const cfg = map[status] || { label: status, variant: "outline", icon: Clock, color: "" };
    const Icon = cfg.icon;
    return (
      <Badge variant={cfg.variant} className={`gap-1 ${cfg.color}`}>
        <Icon className={`h-3 w-3 ${status === 'running' ? 'animate-spin' : ''}`} />
        {cfg.label}
      </Badge>
    );
  };

  const getTypeBadge = (type) => {
    const map = {
      full: { label: "Complet", color: "bg-purple-100 text-purple-700 border-purple-200" },
      database: { label: "Base de données", color: "bg-blue-100 text-blue-700 border-blue-200" },
      uploads: { label: "Fichiers", color: "bg-orange-100 text-orange-700 border-orange-200" },
      workspace: { label: "Workspace", color: "bg-green-100 text-green-700 border-green-200" },
    };
    const cfg = map[type] || { label: type, color: "" };
    return <Badge variant="outline" className={cfg.color}>{cfg.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="backup-admin-tab">
      <Tabs defaultValue="database" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="database" className="gap-2"><Database className="h-4 w-4" /> Base de données</TabsTrigger>
          <TabsTrigger value="export" className="gap-2"><FolderArchive className="h-4 w-4" /> Export Workspace</TabsTrigger>
          <TabsTrigger value="history" className="gap-2"><RotateCcw className="h-4 w-4" /> Historique</TabsTrigger>
        </TabsList>

        <TabsContent value="database" className="space-y-6">
      {/* Stats Overview */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="flex items-center gap-4 p-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-purple-100">
              <Database className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold">{stats.total_backups}</p>
              <p className="text-sm text-muted-foreground">Backups</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100">
              <HardDrive className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold">{formatBytes(stats.total_size_bytes)}</p>
              <p className="text-sm text-muted-foreground">Espace utilisé</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-100">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold">
                {stats.last_backup?.completed_at
                  ? formatRelativeTime(stats.last_backup.completed_at)
                  : "Jamais"}
              </p>
              <p className="text-sm text-muted-foreground">Dernier backup</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-6">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-red-100">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold">{stats.failed_count}</p>
              <p className="text-sm text-muted-foreground">Échecs</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-wrap gap-3">
        <Button className="gap-2" onClick={() => setBackupDialogOpen(true)} data-testid="launch-backup-btn">
          <Play className="h-4 w-4" />
          Lancer un backup
        </Button>
        <Button variant="outline" className="gap-2" onClick={loadData}>
          <RefreshCw className="h-4 w-4" />
          Rafraîchir
        </Button>
      </div>

      {/* Backup History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FolderArchive className="h-5 w-5" />
            Historique des backups
          </CardTitle>
          <CardDescription>Liste de tous les backups effectués</CardDescription>
        </CardHeader>
        <CardContent>
          {jobs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Database className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>Aucun backup effectué</p>
              <p className="text-sm mt-1">Lancez votre premier backup pour commencer</p>
            </div>
          ) : (
            <div className="space-y-3">
              {jobs.map((job) => (
                <div
                  key={job.id}
                  className="flex items-center gap-4 p-4 rounded-lg border bg-card hover:bg-muted/30 transition-colors"
                  data-testid={`backup-job-${job.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {getTypeBadge(job.backup_type)}
                      {getStatusBadge(job.status)}
                      {job.workspace_id && (
                        <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                          <Layers className="h-3 w-3 mr-1" />
                          {getWorkspaceName(job.workspace_id)}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm font-medium mt-1 truncate">
                      {job.description || `Backup ${job.backup_type}`}
                    </p>
                    <div className="flex gap-4 mt-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {job.started_at ? formatRelativeTime(job.started_at) : "-"}
                      </span>
                      {job.size_bytes > 0 && (
                        <span className="flex items-center gap-1">
                          <HardDrive className="h-3 w-3" />
                          {formatBytes(job.size_bytes)}
                        </span>
                      )}
                      <span>Par: {job.initiated_by_name || job.initiated_by}</span>
                    </div>
                    {job.error && (
                      <p className="text-xs text-destructive mt-1 truncate">{job.error}</p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 shrink-0">
                    {job.status === "completed" && (
                      <>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => {
                            const url = backupAPI.downloadUrl(job.id);
                            const token = localStorage.getItem("token");
                            window.open(`${url}?token=${token}`, "_blank");
                          }}
                          title="Télécharger"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-orange-600 hover:text-orange-700"
                          onClick={() => openRestoreDialog(job)}
                          title="Restaurer"
                        >
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                    {job.status !== "running" && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setDeleteJobId(job.id)}
                        title="Supprimer"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Restores */}
      {restoreJobs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5" />
              Restaurations récentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {restoreJobs.map((rj) => (
                <div key={rj.id} className="flex items-center gap-3 p-3 rounded border">
                  {getStatusBadge(rj.status)}
                  <span className="text-sm flex-1">
                    Restauration depuis backup {rj.backup_job_id?.slice(0, 8)}
                    {rj.workspace_id && ` (${getWorkspaceName(rj.workspace_id)})`}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {rj.started_at ? formatRelativeTime(rj.started_at) : ""}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Backup Policy Config */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Politique de backup
          </CardTitle>
          <CardDescription>
            Configurez la rétention et les backups automatiques
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Rétention (jours)</Label>
              <Input
                type="number"
                min={1}
                max={365}
                value={policy.retention_days}
                onChange={(e) => setPolicy({ ...policy, retention_days: parseInt(e.target.value) || 30 })}
              />
              <p className="text-xs text-muted-foreground">Les backups plus anciens seront supprimés automatiquement</p>
            </div>
            <div className="space-y-2">
              <Label>Nombre max. de backups</Label>
              <Input
                type="number"
                min={1}
                max={100}
                value={policy.max_backups}
                onChange={(e) => setPolicy({ ...policy, max_backups: parseInt(e.target.value) || 10 })}
              />
              <p className="text-xs text-muted-foreground">Limite le nombre total de backups conservés</p>
            </div>
          </div>

          <div className="flex items-center justify-between p-4 rounded-lg border">
            <div>
              <p className="font-medium">Backup automatique</p>
              <p className="text-sm text-muted-foreground">
                Planifier des backups automatiques toutes les {policy.auto_backup_interval_hours}h
              </p>
            </div>
            <Switch
              checked={policy.auto_backup_enabled}
              onCheckedChange={(v) => setPolicy({ ...policy, auto_backup_enabled: v })}
            />
          </div>

          {policy.auto_backup_enabled && (
            <div className="space-y-2">
              <Label>Intervalle (heures)</Label>
              <Select
                value={String(policy.auto_backup_interval_hours)}
                onValueChange={(v) => setPolicy({ ...policy, auto_backup_interval_hours: parseInt(v) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="6">Toutes les 6 heures</SelectItem>
                  <SelectItem value="12">Toutes les 12 heures</SelectItem>
                  <SelectItem value="24">Toutes les 24 heures</SelectItem>
                  <SelectItem value="48">Toutes les 48 heures</SelectItem>
                  <SelectItem value="168">Toutes les semaines</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <Button className="gap-2" onClick={handleSavePolicy} disabled={savingPolicy}>
            {savingPolicy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Sauvegarder la politique
          </Button>
        </CardContent>
      </Card>

      {/* =========== DIALOGS =========== */}

      {/* Launch Backup Dialog */}
      <Dialog open={backupDialogOpen} onOpenChange={setBackupDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="h-5 w-5" />
              Lancer un backup
            </DialogTitle>
            <DialogDescription>
              Choisissez le type de backup à effectuer
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Type de backup</Label>
              <Select value={backupType} onValueChange={(v) => { setBackupType(v); if (v !== "workspace") setSelectedWorkspaceId(""); }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Complet (DB + Fichiers)</SelectItem>
                  <SelectItem value="database">Base de données uniquement</SelectItem>
                  <SelectItem value="uploads">Fichiers uniquement</SelectItem>
                  <SelectItem value="workspace">Workspace spécifique</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Workspace selector - only shown when type is "workspace" */}
            {backupType === "workspace" && (
              <div className="space-y-2">
                <Label>Workspace à sauvegarder</Label>
                <Select value={selectedWorkspaceId} onValueChange={setSelectedWorkspaceId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un workspace..." />
                  </SelectTrigger>
                  <SelectContent>
                    {workspaces.map((ws) => (
                      <SelectItem key={ws.id} value={ws.id}>
                        <div className="flex items-center gap-2">
                          <Layers className="h-4 w-4" />
                          {ws.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {workspaces.length === 0 && (
                  <p className="text-xs text-muted-foreground">Aucun workspace disponible</p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label>Description (optionnelle)</Label>
              <Input
                placeholder="Ex: Avant mise à jour v2.0..."
                value={backupDescription}
                onChange={(e) => setBackupDescription(e.target.value)}
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setBackupDialogOpen(false)}>
                Annuler
              </Button>
              <Button
                className="flex-1 gap-2"
                onClick={handleLaunchBackup}
                disabled={launching || (backupType === "workspace" && !selectedWorkspaceId)}
              >
                {launching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                Lancer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Restore Dialog */}
      <Dialog open={restoreDialogOpen} onOpenChange={(v) => { if (!v) { setRestoreDialogOpen(false); setRestoreConfirm(false); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <AlertTriangle className="h-5 w-5" />
              Restauration
            </DialogTitle>
            <DialogDescription>
              Cette action va écraser les données actuelles. Opération irréversible.
            </DialogDescription>
          </DialogHeader>
          {selectedBackup && (
            <div className="space-y-4">
              {/* Backup details */}
              <div className="p-4 rounded-lg border bg-muted/30">
                <p className="text-sm font-medium">Détails du backup</p>
                <div className="mt-2 grid grid-cols-2 gap-2 text-sm text-muted-foreground">
                  <span>Type:</span>
                  <span className="capitalize">{selectedBackup.backup_type}</span>
                  <span>Date:</span>
                  <span>{selectedBackup.completed_at ? new Date(selectedBackup.completed_at).toLocaleString("fr-FR") : "-"}</span>
                  <span>Taille:</span>
                  <span>{formatBytes(selectedBackup.size_bytes)}</span>
                  {selectedBackup.workspace_id && (
                    <>
                      <span>Workspace:</span>
                      <span className="font-medium">{getWorkspaceName(selectedBackup.workspace_id)}</span>
                    </>
                  )}
                </div>
              </div>

              {/* Warning */}
              <div className="p-4 rounded-lg border border-red-200 bg-red-50 text-red-800">
                <p className="text-sm font-medium flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Conséquences de la restauration
                </p>
                <ul className="mt-2 text-sm space-y-1 list-disc pl-5">
                  {selectedBackup.backup_type === "full" && (
                    <>
                      <li>Toutes les données de la base seront remplacées</li>
                      <li>Les fichiers uploadés seront écrasés</li>
                      <li>Les utilisateurs devront peut-être se reconnecter</li>
                    </>
                  )}
                  {selectedBackup.backup_type === "database" && (
                    <li>Toutes les données de la base seront remplacées</li>
                  )}
                  {selectedBackup.backup_type === "uploads" && (
                    <li>Les fichiers uploadés seront écrasés par ceux du backup</li>
                  )}
                  {selectedBackup.backup_type === "workspace" && (
                    <>
                      <li>Les données du workspace <strong>{getWorkspaceName(selectedBackup.workspace_id)}</strong> seront remplacées</li>
                      <li>Les fichiers associés au workspace seront restaurés</li>
                    </>
                  )}
                </ul>
              </div>

              {!restoreConfirm ? (
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" className="flex-1" onClick={() => setRestoreDialogOpen(false)}>
                    Annuler
                  </Button>
                  <Button
                    variant="destructive"
                    className="flex-1 gap-2"
                    onClick={() => setRestoreConfirm(true)}
                  >
                    <AlertTriangle className="h-4 w-4" />
                    Je comprends, continuer
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      Entrez votre mot de passe admin pour confirmer
                    </Label>
                    <Input
                      type="password"
                      placeholder="Mot de passe admin"
                      value={restorePassword}
                      onChange={(e) => setRestorePassword(e.target.value)}
                      data-testid="restore-password-input"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" className="flex-1" onClick={() => { setRestoreDialogOpen(false); setRestoreConfirm(false); }}>
                      Annuler
                    </Button>
                    <Button
                      variant="destructive"
                      className="flex-1 gap-2"
                      onClick={handleRestore}
                      disabled={restoring || !restorePassword}
                      data-testid="confirm-restore-btn"
                    >
                      {restoring ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                      Restaurer maintenant
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteJobId} onOpenChange={() => setDeleteJobId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce backup ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le fichier de backup sera définitivement supprimé. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteBackup} disabled={deleting}>
              {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
        </TabsContent>

        <TabsContent value="export">
          <WorkspaceExportTab />
        </TabsContent>

        <TabsContent value="history">
          <RestoreHistoryTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
